import React, { useEffect, useState } from "react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { useService } from "../../../context/ServiceContext";
import {
  AlertCircle,
  ArrowRight,
  CarFront,
  Check,
  Cog,
  Copy,
  FileJson,
  FileSpreadsheet,
  Info,
  ListCheck,
  Loader,
  Loader2,
  Printer,
  ShieldCheck,
} from "lucide-react";
import * as XLSX from "xlsx";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { isAlphanumeric } from "validator";
import { verifyRcPremiumV1 } from "../../../Services/ApiTestService/apiTestService";

function RcPremiumV1() {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const [vehicleNo, setVehicleNo] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);
  const { serviceName } = useService();

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isAlpha = (value) => {
    if (!value) return false;
    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied JSON!"));

    setIsJsonCopied(true);
    setTimeout(() => setIsJsonCopied(false), 2000);
  };

  const handlercPremiumV2 = async () => {
    if (!profileData) return;

    const validateVehicleNum = isAlpha(vehicleNo);

    if (!vehicleNo) {
      return setInputError("Vehicle Number is Required");
    }

    if (vehicleNo && !validateVehicleNum) {
      return setInputError("⚠️ Please enter alpha numeric in Vehicle No. field");
    }

    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      const res = await verifyRcPremiumV1({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        vehicle_num: vehicleNo.trim()?.toUpperCase(),
      });

      if (res) {
        apiResponse = res;
      }
      setResponse(apiResponse);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.data?.message || "invalid details ");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(res?.data?.message || "something went wrong");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      {/* Hide elements during print */}
      <div className="print:hidden">
        <Header heading={serviceName} />
      </div>

      {isLoading && (
        <div className="flex justify-center mt-6 print:hidden">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center justify-start pb-14 print:p-0 print:m-0">
        {/* ---------------- FORM (Hidden on print) ---------------- */}
        <div className="max-w-4xl w-full mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-100 print:hidden">
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-slate-100 pb-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
              <CarFront size={24} />
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-800">
                RC Verification
              </h2>
              <p className="text-sm text-slate-500">
                Verify vehicle details using the engine and chassis number.
              </p>
            </div>
          </div>

          {/* Inputs */}
          <div className="mt-6 gap-6 flex justify-center items-center">
            {/* Vehicle Number */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Cog size={17} className="text-blue-600" />
                Vehicle Number
              </label>

              <input
                type="text"
                value={vehicleNo}
                onChange={(e) => setVehicleNo(e.target.value)}
                placeholder="Eg: GJ0***1256"
                className="w-full rounded-xl border uppercase border-slate-300 px-4 py-3 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>

            {/* Button */}
            <div className="mt-8 flex ">
              <button
                disabled={isLoading || !vehicleNo}
                onClick={handlercPremiumV2}
                className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-7 py-3 font-semibold text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify Vehicle
                    <ArrowRight
                      size={18}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Error */}
          {inputError && (
            <div className="mt-5 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm font-medium">{inputError}</span>
            </div>
          )}
        </div>

        {/* response message (Hidden on print) */}
        {response && (
          <div
            className={` ${
              response.status?.code === 500 || response.status?.code === 422
                ? "text-red-500"
                : "text-green-600"
            } py-4 text-2xl font-bold print:hidden`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions (Hidden on print) */}
        <div className="w-full flex justify-center print:hidden">
          <div className="bg-green-100 max-w-3xl border border-green-400 text-green-700 p-5 mt-12 rounded-2xl">
            <h3 className="font-bold flex items-center justify-start gap-2 text-lg mb-2">
              <Info /> Important Instructions{" "}
              <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid Vehicle Number. Clicking the Verify button.
              After successful verification, the system will return the response
              in structured format, including contact details associated with
              the provided details. You may review and copy the data for your
              reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl print:block print:w-full print:max-w-none print:mt-0">
            {/* -------- JSON VIEW (Hidden on print) -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto print:hidden">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8 flex items-center gap-1 bg-gray-800 hover:bg-gray-700 px-3 py-1 rounded text-xs"
              >
                {isJsonCopied ? <Check size={14} /> : <Copy size={14} />}
                {isJsonCopied ? "Copied" : "Copy JSON"}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-2" />
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            {/* -------- STRUCTURED RC DATA -------- */}
            <div className="bg-gray-200 p-2 rounded-md print:bg-white print:p-0">
              <h2 className="text-2xl flex items-center justify-center gap-2 font-bold text-blue-500 mb-1 print:hidden">
                <ListCheck />
                RC Data
              </h2>

              <VehicleDetailsViewer response={response} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default RcPremiumV1;

// Helper component to render individual key-value pairs safely
const DetailItem = ({ label, value }) => {
  let displayValue = "N/A";

  if (typeof value === "boolean") {
    displayValue = value ? "Yes" : "No";
  } else if (Array.isArray(value)) {
    displayValue =
      value.length > 0 && value[0] !== "NA" ? value.join(", ") : "N/A";
  } else if (value !== null && value !== undefined && value !== "") {
    displayValue = String(value);
  }

  return (
    <div className="py-2 border-b border-gray-100 last:border-0 flex justify-between items-center text-sm print:py-1 print:text-xs">
      <span className="text-gray-500 font-medium">{label}</span>
      <span className="text-gray-900 font-semibold text-right break-all max-w-[60%]">
        {displayValue}
      </span>
    </div>
  );
};

// Section Card wrapper
const Section = ({ title, icon, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6 print:mb-4 print:shadow-none print:border">
    <div className="bg-gray-50 px-5 py-3 border-b border-gray-200 flex items-center space-x-2 print:py-2">
      <span className="text-lg print:hidden">{icon}</span>
      <h3 className="text-md font-semibold text-gray-800 print:text-sm">{title}</h3>
    </div>
    <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 print:p-3 print:grid-cols-2">
      {children}
    </div>
  </div>
);

function VehicleDetailsViewer({ response }) {
  const [isFormattedCopied, setIsFormattedCopied] = useState(false);

  if (!response || response?.status?.code !== 200) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
        Failed to load vehicle data or invalid response code.
      </div>
    );
  }

  const { message, data } = response;
  const rc = data || {};

  // 1. COPY FORMATTED DATA FUNCTION
  const handleCopyFormattedText = () => {
    const formattedText = `
==================================================
              RC VEHICLE DETAILS
==================================================
Vehicle Reg No : ${rc.regNo || "N/A"}
Status         : ${rc.status || "N/A"}
Owner Name     : ${rc.owner || "N/A"}
Mobile         : ${data?.mobileNo || rc.mobileNumber || "N/A"}
Status As On   : ${rc.statusAsOn || "N/A"}

--------------------------------------------------
1. REGISTRATION & RTO DETAILS
--------------------------------------------------
Registration No        : ${rc.regNo || "N/A"}
RTO Code               : ${rc.rtoCode || "N/A"}
Registration Authority : ${rc.regAuthority || "N/A"}
Registration Date      : ${rc.regDate || "N/A"}
RC Expiry Date         : ${rc.rcExpiryDate || "N/A"}
Vehicle Tax Valid Upto : ${rc.vehicleTaxUpto || "N/A"}
Standard Capacity      : ${rc.rcStandardCap || "N/A"}
Financed Status        : ${rc.financed ? "Yes" : "No"}
RC Financer            : ${rc.rcFinancer || "N/A"}

--------------------------------------------------
2. OWNERSHIP & ADDRESS INFORMATION
--------------------------------------------------
Owner Name             : ${rc.owner || "N/A"}
Owner Count / Serial   : ${rc.ownerCount || "N/A"}
Father's Name          : ${rc.ownerFatherName || "N/A"}
Owner Mobile           : ${rc.mobileNumber || "N/A"}
Present Address        : ${rc.presentAddress || "N/A"}
Permanent Address      : ${rc.permanentAddress || "N/A"}

--------------------------------------------------
3. VEHICLE TECHNICAL SPECIFICATIONS
--------------------------------------------------
Manufacturer           : ${rc.vehicleManufacturerName || "N/A"}
Model Description      : ${rc.model || "N/A"}
Vehicle Class          : ${rc.vehicleClass || "N/A"}
Vehicle Category       : ${rc.vehicleCategory || "N/A"}
Body Type              : ${rc.bodyType || "N/A"}
Color                  : ${rc.vehicleColour || "N/A"}
Fuel Type              : ${rc.type || "N/A"}
Emission Norms         : ${rc.normsType || "N/A"}
Engine Number          : ${rc.engine || "N/A"}
Chassis Number         : ${rc.chassis || "N/A"}
Engine CC              : ${rc.vehicleCubicCapacity || "N/A"}
No. of Cylinders       : ${rc.vehicleCylindersNo || "N/A"}
Mfg Month/Year         : ${rc.vehicleManufacturingMonthYear || "N/A"}
Wheelbase (mm)         : ${rc.wheelbase || "N/A"}
Gross Weight (kg)      : ${rc.grossVehicleWeight || "N/A"}
Unladen Weight (kg)    : ${rc.unladenWeight || "N/A"}
Seating Capacity       : ${rc.vehicleSeatCapacity || "N/A"}
Sleeper Capacity       : ${rc.vehicleSleeperCapacity || "N/A"}
Standing Capacity      : ${rc.vehicleStandingCapacity || "N/A"}
Electric Vehicle       : ${rc.electricVehicle ? "Yes" : "No"}

--------------------------------------------------
4. INSURANCE & PUCC DETAILS
--------------------------------------------------
Insurance Provider     : ${rc.vehicleInsuranceCompanyName || "N/A"}
Insurance Policy No    : ${rc.vehicleInsurancePolicyNumber || "N/A"}
Insurance Valid Upto   : ${rc.vehicleInsuranceUpto || "N/A"}
PUCC Number            : ${rc.puccNumber || "N/A"}
PUCC Valid Upto        : ${rc.puccUpto || "N/A"}
Blacklist Status       : ${rc.blacklistStatus || "N/A"}
Blacklist Details      : ${rc.blacklistDetails || "N/A"}

--------------------------------------------------
5. PERMITS & COMMERCIAL STATUS
--------------------------------------------------
Commercial Vehicle     : ${rc.isCommercial ? "Yes" : "No"}
Permit Number          : ${rc.permitNumber || "N/A"}
Permit Type            : ${rc.permitType || "N/A"}
Permit Issue Date      : ${rc.permitIssueDate || "N/A"}
Permit Valid From      : ${rc.permitValidFrom || "N/A"}
Permit Valid Upto      : ${rc.permitValidUpto || "N/A"}
National Permit No     : ${rc.nationalPermitNumber || "N/A"}
National Permit Upto   : ${rc.nationalPermitUpto || "N/A"}
National Permit By     : ${rc.nationalPermitIssuedBy || "N/A"}

--------------------------------------------------
6. NON-USE & NOC STATUS
--------------------------------------------------
Non-Use Status         : ${rc.nonUseStatus || "N/A"}
Non-Use From           : ${rc.nonUseFrom || "N/A"}
Non-Use To             : ${rc.nonUseTo || "N/A"}
NOC Details            : ${rc.nocDetails || "N/A"}

--------------------------------------------------
7. API REQUEST METADATA
--------------------------------------------------
Request ID             : ${data?.requestId || "N/A"}
Client Request ID      : ${data?.clientRequestId || "N/A"}
Status Message         : ${message || "N/A"}
Partial Data Flag      : ${rc.partialData || "N/A"}
DB Result              : ${rc.dbResult || "N/A"}
MMV Response           : ${rc.mmvResponse || "N/A"}
==================================================
    `.trim();

    navigator.clipboard.writeText(formattedText).then(() => {
      showSuccessToast("Formatted RC data copied!");
      setIsFormattedCopied(true);
      setTimeout(() => setIsFormattedCopied(false), 2000);
    });
  };

  // 2. EXPORT TO EXCEL FUNCTION
  const handleExportToExcel = () => {
    const formattedRows = [
      // Registration & RTO
      { Field: "Registration No", Value: rc.regNo || "N/A" },
      { Field: "RTO Code", Value: rc.rtoCode || "N/A" },
      { Field: "Registration Authority", Value: rc.regAuthority || "N/A" },
      { Field: "Registration Date", Value: rc.regDate || "N/A" },
      { Field: "RC Expiry Date", Value: rc.rcExpiryDate || "N/A" },
      { Field: "Vehicle Tax Valid Upto", Value: rc.vehicleTaxUpto || "N/A" },
      { Field: "Standard Capacity", Value: rc.rcStandardCap || "N/A" },
      { Field: "Financed Status", Value: rc.financed ? "Yes" : "No" },
      { Field: "RC Financer", Value: rc.rcFinancer || "N/A" },

      // Ownership & Address
      { Field: "Owner Name", Value: rc.owner || "N/A" },
      { Field: "Owner Count / Serial", Value: rc.ownerCount || "N/A" },
      { Field: "Father's Name", Value: rc.ownerFatherName || "N/A" },
      { Field: "Owner Mobile", Value: rc.mobileNumber || "N/A" },
      { Field: "Present Address", Value: rc.presentAddress || "N/A" },
      { Field: "Permanent Address", Value: rc.permanentAddress || "N/A" },

      // Vehicle Technical Specifications
      { Field: "Manufacturer", Value: rc.vehicleManufacturerName || "N/A" },
      { Field: "Model Description", Value: rc.model || "N/A" },
      { Field: "Vehicle Class", Value: rc.vehicleClass || "N/A" },
      { Field: "Vehicle Category", Value: rc.vehicleCategory || "N/A" },
      { Field: "Body Type", Value: rc.bodyType || "N/A" },
      { Field: "Color", Value: rc.vehicleColour || "N/A" },
      { Field: "Fuel Type", Value: rc.type || "N/A" },
      { Field: "Emission Norms", Value: rc.normsType || "N/A" },
      { Field: "Engine Number", Value: rc.engine || "N/A" },
      { Field: "Chassis Number", Value: rc.chassis || "N/A" },
      { Field: "Engine CC", Value: rc.vehicleCubicCapacity || "N/A" },
      { Field: "No. of Cylinders", Value: rc.vehicleCylindersNo || "N/A" },
      { Field: "Manufacturing Month/Year", Value: rc.vehicleManufacturingMonthYear || "N/A" },
      { Field: "Wheelbase (mm)", Value: rc.wheelbase || "N/A" },
      { Field: "Gross Vehicle Weight (kg)", Value: rc.grossVehicleWeight || "N/A" },
      { Field: "Unladen Weight (kg)", Value: rc.unladenWeight || "N/A" },
      { Field: "Seating Capacity", Value: rc.vehicleSeatCapacity || "N/A" },
      { Field: "Sleeper Capacity", Value: rc.vehicleSleeperCapacity || "N/A" },
      { Field: "Standing Capacity", Value: rc.vehicleStandingCapacity || "N/A" },
      { Field: "Electric Vehicle", Value: rc.electricVehicle ? "Yes" : "No" },

      // Insurance & PUCC
      { Field: "Insurance Provider", Value: rc.vehicleInsuranceCompanyName || "N/A" },
      { Field: "Insurance Policy Number", Value: rc.vehicleInsurancePolicyNumber || "N/A" },
      { Field: "Insurance Valid Upto", Value: rc.vehicleInsuranceUpto || "N/A" },
      { Field: "PUCC Number", Value: rc.puccNumber || "N/A" },
      { Field: "PUCC Valid Upto", Value: rc.puccUpto || "N/A" },
      { Field: "Blacklist Status", Value: rc.blacklistStatus || "N/A" },
      { Field: "Blacklist Details", Value: rc.blacklistDetails || "N/A" },

      // Permits & Commercial Status
      { Field: "Commercial Vehicle", Value: rc.isCommercial ? "Yes" : "No" },
      { Field: "Permit Number", Value: rc.permitNumber || "N/A" },
      { Field: "Permit Type", Value: rc.permitType || "N/A" },
      { Field: "Permit Issue Date", Value: rc.permitIssueDate || "N/A" },
      { Field: "Permit Valid From", Value: rc.permitValidFrom || "N/A" },
      { Field: "Permit Valid Upto", Value: rc.permitValidUpto || "N/A" },
      { Field: "National Permit No", Value: rc.nationalPermitNumber || "N/A" },
      { Field: "National Permit Upto", Value: rc.nationalPermitUpto || "N/A" },
      { Field: "National Permit Issued By", Value: rc.nationalPermitIssuedBy || "N/A" },

      // Non-Use & NOC
      { Field: "Non-Use Status", Value: rc.nonUseStatus || "N/A" },
      { Field: "Non-Use From", Value: rc.nonUseFrom || "N/A" },
      { Field: "Non-Use To", Value: rc.nonUseTo || "N/A" },
      { Field: "NOC Details", Value: rc.nocDetails || "N/A" },

      // Request Metadata
      { Field: "Request ID", Value: data?.requestId || "N/A" },
      { Field: "Client Request ID", Value: data?.clientRequestId || "N/A" },
      { Field: "Status Message", Value: message || "N/A" },
      { Field: "Partial Data Flag", Value: rc.partialData || "N/A" },
      { Field: "DB Result", Value: rc.dbResult || "N/A" },
      { Field: "MMV Response", Value: rc.mmvResponse || "N/A" }
    ];

    const worksheet = XLSX.utils.json_to_sheet(formattedRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RC Details");

    // Set column widths for better excel viewing
    worksheet["!cols"] = [{ wch: 25 }, { wch: 30 }, { wch: 45 }];

    XLSX.writeFile(workbook, `RC_Details_${rc.regNo || "Vehicle"}.xlsx`);
    showSuccessToast("Excel file downloaded successfully!");
  };

  // 3. PRINT FUNCTION
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="max-w-6xl mx-auto p-2 min-h-screen">
      {/* Action Buttons Header Bar (Export, Print, Copy) */}
      <div className="flex flex-wrap justify-center items-center gap-3 mb-4 print:hidden">
        <button
          onClick={handleCopyFormattedText}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-white text-slate-700 hover:bg-slate-50 border border-slate-300 font-semibold text-xs rounded-xl shadow-sm transition"
        >
          {isFormattedCopied ? <Check size={15} className="text-green-600" /> : <Copy size={15} />}
          {isFormattedCopied ? "Copied Data" : "Copy Formatted"}
        </button>

        <button
          onClick={handleExportToExcel}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs rounded-xl shadow-sm transition"
        >
          <FileSpreadsheet size={15} />
          Export to Excel
        </button>

        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-xl shadow-sm transition"
        >
          <Printer size={15} />
          Print
        </button>
      </div>

      {/* Header Banner */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm mb-6 flex flex-wrap justify-between items-center gap-4 print:shadow-none print:border print:p-4">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="text-2xl font-bold text-gray-900">{rc.regNo || "N/A"}</h1>
            <span
              className={`px-3 py-1 rounded-full text-xs font-bold ${
                rc.status === "ACTIVE"
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {rc.status || "UNKNOWN"}
            </span>
          </div>
          <p className="text-gray-600 text-sm mt-1">
            {rc.vehicleManufacturerName} {rc.model}
          </p>
        </div>
        <div className="text-right text-xs text-gray-500">
          <div>
            Status as on:{" "}
            <span className="font-medium text-gray-700">
              {rc.statusAsOn || "N/A"}
            </span>
          </div>
          {/* <div>
            Mobile:{" "}
            <span className="font-medium text-gray-700">
              {data?.mobileNo || rc.mobileNumber || "N/A"}
            </span>
          </div> */}
        </div>
      </div>

      {/* 1. Registration & RTO Details */}
      <Section title="Registration & RTO Details" icon="📋">
        <DetailItem label="Registration No" value={rc.regNo} />
        <DetailItem label="RTO Code" value={rc.rtoCode} />
        <DetailItem label="Registration Authority" value={rc.regAuthority} />
        <DetailItem label="Registration Date" value={rc.regDate} />
        <DetailItem label="RC Expiry Date" value={rc.rcExpiryDate} />
        <DetailItem label="Vehicle Tax Valid Upto" value={rc.vehicleTaxUpto} />
        <DetailItem label="Standard Capacity" value={rc.rcStandardCap} />
        <DetailItem label="Financed Status" value={rc.financed} />
        <DetailItem label="RC Financer" value={rc.rcFinancer} />
      </Section>

      {/* 2. Owner & Contact Information */}
      <Section title="Ownership & Address Information" icon="👤">
        <DetailItem label="Owner Name" value={rc.owner} />
        <DetailItem label="Owner Count / Serial" value={rc.ownerCount} />
        <DetailItem label="Father's Name" value={rc.ownerFatherName} />
        <DetailItem label="Owner Mobile" value={rc.mobileNumber} />
        <DetailItem label="Present Address" value={rc.presentAddress} />
        <DetailItem label="Permanent Address" value={rc.permanentAddress} />
      </Section>

      {/* 3. Vehicle Specifications */}
      <Section title="Vehicle Technical Specifications" icon="⚙️">
        <DetailItem label="Manufacturer" value={rc.vehicleManufacturerName} />
        <DetailItem label="Model Description" value={rc.model} />
        <DetailItem label="Vehicle Class" value={rc.vehicleClass} />
        <DetailItem label="Vehicle Category" value={rc.vehicleCategory} />
        <DetailItem label="Body Type" value={rc.bodyType} />
        <DetailItem label="Color" value={rc.vehicleColour} />
        <DetailItem label="Fuel Type" value={rc.type} />
        <DetailItem label="Emission Norms" value={rc.normsType} />
        <DetailItem label="Engine Number" value={rc.engine} />
        <DetailItem label="Chassis Number" value={rc.chassis} />
        <DetailItem label="Engine CC" value={rc.vehicleCubicCapacity} />
        <DetailItem label="No. of Cylinders" value={rc.vehicleCylindersNo} />
        <DetailItem label="Manufacturing Month/Year" value={rc.vehicleManufacturingMonthYear} />
        <DetailItem label="Wheelbase (mm)" value={rc.wheelbase} />
        <DetailItem label="Gross Vehicle Weight (kg)" value={rc.grossVehicleWeight} />
        <DetailItem label="Unladen Weight (kg)" value={rc.unladenWeight} />
        <DetailItem label="Seating Capacity" value={rc.vehicleSeatCapacity} />
        <DetailItem label="Sleeper Capacity" value={rc.vehicleSleeperCapacity} />
        <DetailItem label="Standing Capacity" value={rc.vehicleStandingCapacity} />
        <DetailItem label="Electric Vehicle" value={rc.electricVehicle} />
      </Section>

      {/* 4. Insurance & Compliance */}
      <Section title="Insurance & PUCC Details" icon="🛡️">
        <DetailItem label="Insurance Provider" value={rc.vehicleInsuranceCompanyName} />
        <DetailItem label="Insurance Policy Number" value={rc.vehicleInsurancePolicyNumber} />
        <DetailItem label="Insurance Valid Upto" value={rc.vehicleInsuranceUpto} />
        <DetailItem label="PUCC Number" value={rc.puccNumber} />
        <DetailItem label="PUCC Valid Upto" value={rc.puccUpto} />
        <DetailItem label="Blacklist Status" value={rc.blacklistStatus} />
        <DetailItem label="Blacklist Details" value={rc.blacklistDetails} />
      </Section>

      {/* 5. Permit & Commercial Info */}
      <Section title="Permits & Commercial Status" icon="🚛">
        <DetailItem label="Commercial Vehicle" value={rc.isCommercial} />
        <DetailItem label="Permit Number" value={rc.permitNumber} />
        <DetailItem label="Permit Type" value={rc.permitType} />
        <DetailItem label="Permit Issue Date" value={rc.permitIssueDate} />
        <DetailItem label="Permit Valid From" value={rc.permitValidFrom} />
        <DetailItem label="Permit Valid Upto" value={rc.permitValidUpto} />
        <DetailItem label="National Permit No" value={rc.nationalPermitNumber} />
        <DetailItem label="National Permit Upto" value={rc.nationalPermitUpto} />
        <DetailItem label="National Permit Issued By" value={rc.nationalPermitIssuedBy} />
      </Section>

      {/* 6. Non-Use & NOC Info */}
      <Section title="Non-Use & NOC Status" icon="🚫">
        <DetailItem label="Non-Use Status" value={rc.nonUseStatus} />
        <DetailItem label="Non-Use From" value={rc.nonUseFrom} />
        <DetailItem label="Non-Use To" value={rc.nonUseTo} />
        <DetailItem label="NOC Details" value={rc.nocDetails} />
      </Section>

      {/* 7. Request Metadata */}
      <Section title="API Request Metadata" icon="⚙️">
        <DetailItem label="Request ID" value={data?.requestId} />
        <DetailItem label="Client Request ID" value={data?.clientRequestId} />
        <DetailItem label="Status Message" value={message} />
        <DetailItem label="Partial Data Flag" value={rc.partialData} />
        <DetailItem label="DB Result" value={rc.dbResult} />
        <DetailItem label="MMV Response" value={rc.mmvResponse} />
      </Section>
    </div>
  );
}